import React, { useEffect, useRef, useState } from 'react';
import { supabase } from '../../services/supabaseClient';
import { Contact, ReportMetadata } from '../../types';
import { Arbeitsschritt, Gefaehrdung, Schutzmassnahme, SigeBlock, SigeProject, planHatZeitachse } from './types';
import { PlankopfForm, plankopfAusStammdaten, plankopfBereit, plankopfDarstellung, plankopfZuDb } from './uebernahme';
import { ANLAGE_ABGEBROCHEN_TEXT, sigePlanVorlagenFuerAnlage, vorbelegungNeuerPlan } from '../../services/sigePlanVorlagen';
import { markiereUebernommen } from '../../services/planungsphaseService';
import { describeError } from '../../services/errorMessage';
import { pruefeSigePlanVollstaendigkeit } from './vollstaendigkeit';
import { usePlanungFuerSigePlan, type SigeHinweis } from './planungAnbindung';
import { WizardStepPlankopf } from './WizardStepPlankopf';
import { WizardStepGrundlagen } from './WizardStepGrundlagen';
import { WizardStepAbschnitte } from './WizardStepAbschnitte';
import { WizardStepGefaehrdungen } from './WizardStepGefaehrdungen';
import { WizardStepBloecke } from './WizardStepBloecke';
import { WizardStepPruefung } from './WizardStepPruefung';
import { Button } from '../Button';
import { ArrowLeft, ArrowRight, CheckSquare, Loader2, Wand2, X } from 'lucide-react';

/**
 * Geführte Anlage eines SiGe-Plans in sechs Schritten.
 *
 * Der Editor bleibt die Expertenansicht — der Assistent ist der Weg vom leeren
 * Projekt zum vorzeigbaren Plan. Er hält den Datensatz-Zustand selbst (Kopf,
 * Schritte, Blöcke), damit jeder Schritt auf dem Ergebnis des vorigen aufsetzt;
 * beim Schließen lädt der Editor einmal neu.
 *
 * Wichtig: der Kopf-Datensatz entsteht erst am Ende von Schritt 1. Vorher gibt
 * es keine `sige_projekt_id`, weshalb die Schritte 2–6 ohne ihn nicht laufen
 * können (Fremdschlüssel).
 *
 * Die Planungsphase liefert über `usePlanungFuerSigePlan` nur VORSCHLÄGE
 * (Beschreibung, Vorauswahl der Musterzeilen, bedingte Dokumente, Hinweise,
 * Anhang-II-Prüfung). Übernommen wird in jedem Schritt per Klick; der Stempel
 * „berücksichtigt" (`markiereUebernommen`) ist der einzige Schreibweg zurück.
 */

const TITEL = [
  'Plankopf & Rahmen',
  'Grundlagen einlesen',
  'Abschnitte & Gewerke',
  'Gefährdungen & Maßnahmen',
  'Plan-Blöcke',
  'Prüfen & Freigeben',
];

interface Props {
  projectId: string;
  projectName?: string;
  reportMeta?: ReportMetadata;
  beteiligte: Contact[];
  sigeProject: SigeProject | null;
  arbeitsschritte: Arbeitsschritt[];
  bloecke: SigeBlock[];
  gefaehrdungen: Record<string, Gefaehrdung[]>;
  massnahmen: Record<string, Schutzmassnahme[]>;
  hauptabschnitte: string[];
  startSchritt?: number;
  /** `geaendert` = der Editor muss neu laden. */
  onClose: (geaendert: boolean) => void;
  /** Nach „Abschließen": zurück in den Export-/Abrechnungs-Flow. */
  onAbgeschlossen: () => void;
}

export const SiGePlanWizard: React.FC<Props> = ({
  projectId, projectName, reportMeta, beteiligte, sigeProject, arbeitsschritte, bloecke,
  gefaehrdungen, massnahmen, hauptabschnitte, startSchritt = 1, onClose, onAbgeschlossen,
}) => {
  const [schritt, setSchritt] = useState(sigeProject ? startSchritt : 1);
  const [plan, setPlan] = useState<SigeProject | null>(sigeProject);
  const [schritte, setSchritte] = useState<Arbeitsschritt[]>(arbeitsschritte);
  const [planBloecke, setPlanBloecke] = useState<SigeBlock[]>(bloecke);
  const [form, setForm] = useState<PlankopfForm>(() => plankopfAusStammdaten(reportMeta, projectName, sigeProject));
  const [hinweis, setHinweis] = useState<{ text: string; ton: 'info' | 'fehler' } | null>(null);
  const [busy, setBusy] = useState(false);
  const [geaendert, setGeaendert] = useState(false);
  // Laufsperre für Schritt 1 (siehe sichereSchritt1).
  const schritt1LaeuftRef = useRef(false);
  // Projektsteckbrief der Planungsphase — nur lesen; Lesefehler stehen in `fehler`.
  const planung = usePlanungFuerSigePlan(projectId, reportMeta ?? null);

  const melde = (text: string, ton: 'info' | 'fehler' = 'info') => setHinweis({ text, ton });

  /** „Berücksichtigt": stempelt die Planungsfrage (`uebernommen_am`) und liest die Planung neu. */
  const hinweisBeruecksichtigt = async (h: SigeHinweis) => {
    if (!h.frageId) {
      melde(`Hinweis ${h.code} lässt sich keiner Planungsfrage zuordnen. Bitte in der Planungsphase prüfen.`, 'fehler');
      return;
    }
    try {
      const n = await markiereUebernommen([h.frageId], new Date().toISOString());
      melde(n > 0
        ? `Hinweis ${h.code} in der Planungsphase als berücksichtigt gestempelt.`
        : `Hinweis ${h.code} war bereits als berücksichtigt gestempelt.`);
      planung.neuLaden();
    } catch (e: unknown) {
      console.error('Stempel „berücksichtigt" fehlgeschlagen', e);
      melde(`Der Hinweis konnte nicht gestempelt werden: ${describeError(e)}`, 'fehler');
    }
  };

  // Ohne Kopf-Datensatz ist kein Folgeschritt möglich (Fremdschlüssel fehlt).
  useEffect(() => {
    if (!plan && schritt > 1) setSchritt(1);
  }, [plan, schritt]);

  /** Liest Schritte und Blöcke der aktuellen Version frisch — nach jeder Übernahme. */
  const ladeStand = async (planId: string) => {
    // Gerufen wird immer NACH einem Schreibvorgang: der Editor muss danach neu
    // laden, auch wenn das anschließende Lesen hier scheitert — sonst blieben
    // gerade geschriebene Zeilen nach dem Schließen unsichtbar.
    setGeaendert(true);
    try {
      const [{ data: steps, error: stepError }, { data: blocks, error: blockError }] = await Promise.all([
        supabase.from('sige_arbeitsschritte').select('*').eq('sige_projekt_id', planId).order('sortierung'),
        supabase.from('sige_bloecke').select('*').eq('sige_projekt_id', planId),
      ]);
      if (stepError) throw stepError;
      if (blockError) throw blockError;
      setSchritte((steps || []) as Arbeitsschritt[]);
      setPlanBloecke((blocks || []) as SigeBlock[]);
    } catch (e: any) {
      console.error('Assistent: Stand konnte nicht gelesen werden', e);
      melde(`Der aktuelle Stand konnte nicht gelesen werden: ${e?.message || String(e)}`, 'fehler');
    }
  };

  /**
   * Legt den Kopf-Datensatz an oder schreibt ihn fort. `mitStammdaten` ist der
   * „Später"-Weg: dann gelten die Vorbelegungen aus dem Projekt, genau wie
   * früher bei der stillen Neuanlage im Editor.
   */
  const sichereSchritt1 = async (mitStammdaten = false): Promise<boolean> => {
    // Laufsperre: die Neuanlage wartet ggf. auf die Plotter-Vorlagen (bis 5 s).
    // `busy` sperrt die Knöpfe erst mit dem nächsten Rendern; die Ref hält
    // auch einen zweiten Aufruf davor ab — sonst entstünden zwei Kopfsätze.
    if (schritt1LaeuftRef.current) return false;
    schritt1LaeuftRef.current = true;
    const zuSichern = mitStammdaten ? plankopfAusStammdaten(reportMeta, projectName, form) : form;
    setBusy(true);
    setHinweis(null);
    try {
      if (plan) {
        // Darstellung nur, wenn sich der Haken „Bauzeitenplan liegt noch nicht
        // vor" geändert hat — und dann auf dem gelesenen Stand des Plans, damit
        // Farben, Schrift und Verantwortliche aus dem Stempelfeld bleiben.
        const darstellung = plankopfDarstellung(zuSichern, plan);
        const { data, error } = await supabase.from('sige_projekte')
          .update({ ...plankopfZuDb(zuSichern), ...(darstellung ? { darstellung } : {}) })
          .eq('id', plan.id).select().maybeSingle();
        if (error) throw error;
        if (data) setPlan(data as SigeProject);
        setGeaendert(true);
        return true;
      }

      // Neuanlage: Plotterbreite = Standardbreite der Firma. Nur hier, nicht
      // beim Fortschreiben oben — dort bleibt die Breite des Plans unberührt.
      // Gespeichert, damit der Plan seine Breite behält, wenn die Firma die
      // Standardbreite später ändert. Deshalb NICHT synchron aus dem Store:
      // läuft das Laden nach dem Login noch, wird darauf gewartet (höchstens
      // 5 s) — sonst bekäme der Plan dauerhaft die Werkseinstellung.
      const anlage = await sigePlanVorlagenFuerAnlage();
      if (anlage.abgebrochen === true) {
        // Abgemeldet oder anderer Nutzer während des Wartens: nichts anlegen.
        melde(ANLAGE_ABGEBROCHEN_TEXT, 'fehler');
        return false;
      }
      const vorbelegung = vorbelegungNeuerPlan(anlage.vorlagen);
      const breiteHinweis = anlage.geladen
        ? null
        : `Die Firmenvorgabe für die Plotterbreite konnte nicht geladen werden. Der Plan wurde mit ${vorbelegung.plot_breite_mm} mm angelegt. Die Breite lässt sich im Plankopf ändern.`;
      // Mit Haken „Bauzeitenplan liegt noch nicht vor": { zeitachse: false }.
      const darstellung = plankopfDarstellung(zuSichern, null);
      const { data, error } = await supabase.from('sige_projekte')
        .insert({ ...plankopfZuDb(zuSichern, projectId), ...vorbelegung, ...(darstellung ? { darstellung } : {}) })
        .select().maybeSingle();

      if (error) {
        // Resilienter Fallback wie bisher im Editor: fehlt eine Spalte, wird der
        // Plan wenigstens minimal angelegt — sonst steht der Nutzer vor nichts.
        console.warn('Vollständige Plan-Neuanlage fehlgeschlagen, minimaler Fallback:', error.message);
        const { data: minimal, error: fallbackError } = await supabase.from('sige_projekte').insert({
          project_id: projectId,
          baumassnahme_name: (zuSichern.baumassnahme_name || projectName || 'SiGe-Projekt').trim(),
          plan_inhalt: 'Sicherheits- und Gesundheitsschutzplan',
          // Die Spalte gibt es seit jeher (das Stempelfeld schrieb sie immer) —
          // sie gefährdet den Rückfall nicht, und auch dieser Plan bekommt die
          // Standardbreite statt eines Datenbank-Defaults.
          ...vorbelegung,
        }).select().maybeSingle();
        if (fallbackError) {
          throw new Error(`${error.message} / Fallback: ${fallbackError.message}`);
        }
        setPlan(minimal as SigeProject);
        setGeaendert(true);
        melde(
          ['Der Plan wurde nur mit Grunddaten angelegt. Bitte den Plankopf im Editor prüfen.', breiteHinweis].filter(Boolean).join('\n'),
          'fehler',
        );
        return true;
      }

      if (!data) {
        // RLS erlaubt hier oft INSERT, aber nicht die SELECT-Rückgabe danach —
        // dann liefert `.select().maybeSingle()` `null`, obwohl die Zeile
        // angelegt wurde. Ohne Nachlese würde `setPlan(null)` bei erneutem
        // „Weiter" einen ZWEITEN Kopfsatz anlegen (kein Datensatz zum Abgleich
        // vorhanden). Erst nachlesen — ein zweiter Insert bei Retry entfällt,
        // weil dieser Nachlese-Pfad zuerst greift.
        const { data: nachgelesen, error: nachleseError } = await supabase
          .from('sige_projekte')
          .select('*')
          .eq('project_id', projectId)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        if (nachleseError || !nachgelesen) {
          melde('Der Plan wurde angelegt, konnte aber nicht zurückgelesen werden. Bitte den Editor neu laden, bevor erneut auf „Weiter" geklickt wird.', 'fehler');
          return false;
        }
        setPlan(nachgelesen as SigeProject);
        setGeaendert(true);
        if (breiteHinweis) melde(breiteHinweis);
        return true;
      }

      setPlan(data as SigeProject);
      setGeaendert(true);
      if (breiteHinweis) melde(breiteHinweis);
      return true;
    } catch (e: any) {
      console.error('Plankopf konnte nicht gespeichert werden', e);
      melde(`Der Plan konnte nicht gespeichert werden: ${e?.message || String(e)}`, 'fehler');
      return false;
    } finally {
      setBusy(false);
      schritt1LaeuftRef.current = false;
    }
  };

  const weiter = async (mitStammdaten = false) => {
    setHinweis(null);
    if (schritt === 1) {
      const ok = await sichereSchritt1(mitStammdaten);
      if (!ok) return;
    }
    setSchritt(s => Math.min(6, s + 1));
  };

  const abschliessen = async () => {
    if (!plan) return;
    setBusy(true);
    try {
      const pruefung = pruefeSigePlanVollstaendigkeit({
        plan, arbeitsschritte: schritte, bloecke: planBloecke, anhang2Nummern: planung.anhang2Nummern,
      });
      // „Geprüft" nur, wenn keine harte Lücke mehr offen ist — sonst bleibt es
      // ein Entwurf, auch wenn der Assistent durchlaufen wurde.
      const status: SigeProject['status'] = pruefung.blockierend.length === 0 ? 'geprueft' : 'entwurf';
      const { error } = await supabase.from('sige_projekte').update({ status }).eq('id', plan.id);
      if (error) throw error;
      onClose(true);
      onAbgeschlossen();
    } catch (e: any) {
      console.error('Abschluss fehlgeschlagen', e);
      melde(`Der Status konnte nicht gesetzt werden: ${e?.message || String(e)}`, 'fehler');
      setBusy(false);
    }
  };

  const weiterErlaubt = schritt !== 1 || plankopfBereit(form);
  const zeitraum = { von: plan?.zeitraum_von || form.zeitraum_von, bis: plan?.zeitraum_bis || form.zeitraum_bis };
  // Ohne Zeitachse (Bauzeitenplan liegt noch nicht vor, so wie gespeichert)
  // bekommen übernommene Zeilen keinen Zeitraum: die KI-Daten fallen weg
  // (`ohneZeitachse`), und die Musterzeilen schätzen ohne Bauzeit nichts aus
  // ihrer Phase (`zeitfensterFuerAbschnitt` bleibt leer).
  const ohneZeitachse = !planHatZeitachse(plan ? plan.darstellung : form.darstellung);

  return (
    <div className="fixed inset-0 z-[150] bg-slate-900/90 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl h-[92vh] flex flex-col overflow-hidden">

        <div className="bg-slate-50 p-5 border-b border-slate-200 flex justify-between items-start gap-4 shrink-0">
          <div className="min-w-0">
            <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
              <Wand2 className="w-5 h-5 text-blue-600" />
              SiGe-Plan Assistent
            </h2>
            <p className="text-sm text-slate-500 mt-1">Schritt {schritt} von 6: {TITEL[schritt - 1]}</p>
            <div data-tutorial="sigeplan.wizard-schritte" className="flex gap-1.5 mt-3">
              {TITEL.map((t, i) => (
                <div
                  key={t}
                  title={`${i + 1}. ${t}`}
                  className={`h-1.5 w-10 rounded-full transition-colors ${
                    i + 1 < schritt ? 'bg-blue-500' : i + 1 === schritt ? 'bg-blue-600' : 'bg-slate-200'
                  }`}
                />
              ))}
            </div>
          </div>
          <button
            onClick={() => onClose(geaendert)}
            className="p-2 hover:bg-slate-200 rounded-full text-slate-400 hover:text-slate-700 transition-colors shrink-0"
            title="Später fortsetzen, bereits Gespeichertes bleibt erhalten"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {hinweis && (
          <div
            className={`px-5 py-2.5 border-b text-xs flex justify-between items-start gap-3 shrink-0 ${
              hinweis.ton === 'fehler'
                ? 'bg-red-50 border-red-200 text-red-700'
                : 'bg-blue-50 border-blue-200 text-blue-800'
            }`}
          >
            <span className="whitespace-pre-wrap">{hinweis.text}</span>
            <button onClick={() => setHinweis(null)} className="px-1 shrink-0 opacity-60 hover:opacity-100" title="Meldung schließen">✕</button>
          </div>
        )}

        {planung.fehler && (
          <div className="px-5 py-2 border-b border-amber-200 bg-amber-50 text-xs text-amber-900 shrink-0">
            Planungsphase nicht vollständig gelesen. Vorschläge daraus fehlen oder gelten als ungeklärt: {planung.fehler}
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-6">
          {schritt === 1 && (
            <WizardStepPlankopf form={form} onChange={setForm} reportMeta={reportMeta} projektName={projectName} />
          )}

          {schritt === 2 && plan && (
            <WizardStepGrundlagen
              sigeProjektId={plan.id}
              zeitraum={zeitraum}
              ohneZeitachse={ohneZeitachse}
              hauptabschnitte={hauptabschnitte}
              vorhandeneBezeichnungen={schritte.map(s => s.bezeichnung)}
              startSortierung={schritte.length}
              beschreibungVorschlag={planung.beschreibung}
              hinweise={planung.hinweise}
              onHinweisBeruecksichtigt={hinweisBeruecksichtigt}
              onUebernommen={() => ladeStand(plan.id)}
              onMitMusterWeiter={() => setSchritt(3)}
              melde={melde}
            />
          )}

          {schritt === 3 && plan && (
            <WizardStepAbschnitte
              sigeProjektId={plan.id}
              zeitraum={ohneZeitachse ? {} : zeitraum}
              schritte={schritte}
              beteiligte={beteiligte}
              merkmale={planung.merkmale}
              planungStand={planung.stand}
              onUebernommen={() => ladeStand(plan.id)}
              melde={melde}
            />
          )}

          {schritt === 4 && plan && (
            <WizardStepGefaehrdungen
              schritte={schritte}
              onUebernommen={() => ladeStand(plan.id)}
              melde={melde}
            />
          )}

          {schritt === 5 && plan && (
            <WizardStepBloecke
              sigeProjektId={plan.id}
              bloecke={planBloecke}
              adresse={plan.baumassnahme_adresse || form.baumassnahme_adresse}
              beteiligte={beteiligte}
              onGeaendert={() => ladeStand(plan.id)}
              melde={melde}
            />
          )}

          {schritt === 6 && plan && (
            <WizardStepPruefung
              plan={plan}
              schritte={schritte}
              bloecke={planBloecke}
              gefaehrdungen={gefaehrdungen}
              massnahmen={massnahmen}
              anhang2Nummern={planung.anhang2Nummern}
              hinweise={planung.hinweise}
              onHinweisBeruecksichtigt={hinweisBeruecksichtigt}
              melde={melde}
            />
          )}
        </div>

        <div className="p-4 bg-slate-50 border-t border-slate-200 flex items-center justify-between gap-3 shrink-0">
          <Button
            variant="ghost"
            onClick={() => setSchritt(s => Math.max(1, s - 1))}
            disabled={schritt === 1 || busy}
            className="text-sm"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Zurück
          </Button>

          <div className="flex items-center gap-3">
            <button
              onClick={() => onClose(geaendert)}
              className="text-sm font-semibold text-slate-500 hover:text-slate-800 px-3 py-2"
            >
              Später fortsetzen
            </button>

            {schritt < 6 ? (
              <>
                <Button variant="secondary" onClick={() => weiter(schritt === 1)} disabled={busy} className="text-sm">
                  Später
                </Button>
                <Button data-tutorial="sigeplan.weiter" variant="primary" onClick={() => weiter(false)} disabled={busy || !weiterErlaubt} className="text-sm">
                  {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Weiter <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </>
            ) : (
              <Button data-tutorial="sigeplan.abschliessen" variant="primary" onClick={abschliessen} disabled={busy || !plan} className="text-sm">
                {busy ? <Loader2 className="w-4 h-4 mr-2" /> : <CheckSquare className="w-4 h-4 mr-2" />}
                Abschließen
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
